import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { SlotContentInfo, SlotInfo, TemplateInfo } from '../../model/appSiteMetadata.js';
import { splitByPlaceholders } from './loaderJsonHelper.js';

/**
 * HTML to JSON conversion logic
 * Port of csharp/AssemblerJson/Loader/ParseHtmlJson.cs
 */
export class ParseHtmlJson {

    // #region Parse Html Json

    /**
     * Parses HTML JSON from file content using centralized JsonUtility
     * Returns Object with preserved types (bool, number, string, array, object)
     * Matches C# ParseHtmlJson.Parse behavior
     */
    static parse(jsonContent: string): JsonObject {
        return new JsonObject(JsonUtility.parseJsonToJsonObject(jsonContent));
    }

    // #endregion

    // #region Parse Template Structure

    /**
     * Parses HTML template to extract structure metadata
     */
    static parseTemplateStructure(htmlContent: string, fileName: string, appSiteName: string, fileNameWithoutExt: string): TemplateInfo {
        const templateInfo = new TemplateInfo();
        templateInfo.htmlFileName = fileName;
        templateInfo.templateName = fileNameWithoutExt; // Actual template name from file
        templateInfo.templateAppSite = appSiteName; // AppSite where this template is defined
        templateInfo.placeholders = [];
        templateInfo.slotholders = [];
        templateInfo.slots = [];

        // Use LoaderJsonHelper utility for splitting by placeholders
        const parts = splitByPlaceholders(htmlContent);

        // Parse structure
        ParseHtmlJson._parseTemplateContent(
            parts,
            0,
            templateInfo.placeholders,
            templateInfo.slotholders,
            templateInfo.slots,
            templateInfo.jsonArrayKeys,
            templateInfo.conditionalKeys
        );

        // Determine template type
        const hasRegularPlaceholders = templateInfo.placeholders.length > 0;
        const hasSlotholders = templateInfo.slotholders.length > 0;
        const hasSlots = templateInfo.slots.length > 0;

        if (hasSlotholders && hasRegularPlaceholders) {
            templateInfo.templateType = 'PlaceHolder+Slotholder';
        } else if (hasSlotholders) {
            templateInfo.templateType = 'Slotholder';
        } else if (hasSlots) {
            templateInfo.templateType = 'Slotted';
        } else if (hasRegularPlaceholders) {
            templateInfo.templateType = 'PlaceHolder';
        } else {
            templateInfo.templateType = 'Normal';
        }

        return templateInfo;
    }

    // #region Parse Template Content

    /**
     * Recursively parses template content to build slot hierarchy
     */
    private static _parseTemplateContent(
        parts: string[],
        startIndex: number,
        placeholders: string[],
        slotholders: SlotInfo[],
        slots: string[],
        arrayKeys: string[],
        conditionalKeys: string[]
    ): { consumed: number } {
        let i = startIndex;

        while (i < parts.length) {
            const part = parts[i];

            if (!part || part.length === 0) {
                i++;
                continue;
            }

            // Check for slot open: {{#SlotName}}
            if (part.startsWith('{{#') && part.endsWith('}}')) {
                const slotName = part.substring(3, part.length - 2);
                i++; // Move past {{#SlotName}}

                // Parse slot content until {{/SlotName}}
                const slotResult = ParseHtmlJson._parseSlotStructure(parts, i, slotName, placeholders);
                i += slotResult.consumed;

                slotholders.push(slotResult.slotInfo);
            }
            // Check for slot marker: {{$HTMLPLACEHOLDER}} specifically
            else if (part === '{{$HTMLPLACEHOLDER}}') {
                const slot = part.substring(2, part.length - 2); // Include the $ prefix
                if (!slots.includes(slot)) {
                    slots.push(slot);
                }
                i++;
            }
            // Check for JSON key reference: {{$KeyName}} - these are NOT slots
            else if (part.startsWith('{{$') && part.endsWith('}}')) {
                // JSON keys are handled separately - they don't go into slots list
                // They will be processed later in ClassifyPlaceholders
                i++;
            }
            // Check for array iteration: {{@ArrayKey}}
            else if (part.startsWith('{{@') && part.endsWith('}}')) {
                const arrayKey = part.substring(3, part.length - 2);
                if (!arrayKeys.includes(arrayKey)) {
                    arrayKeys.push(arrayKey);
                }
                i++; // Move past {{@ArrayKey}}

                // Parse array block content to find nested conditionals and keys
                const arrayResult = ParseHtmlJson._parseArrayBlockMeta(parts, i, arrayKey, false, placeholders, arrayKeys, conditionalKeys);
                i += arrayResult.consumed;
            }
            // Check for conditional block: {{?CondKey}}
            else if (part.startsWith('{{?') && part.endsWith('}}')) {
                const condKey = part.substring(3, part.length - 2);
                if (!conditionalKeys.includes(condKey)) {
                    conditionalKeys.push(condKey);
                }
                i++; // Move past {{?CondKey}}

                // Parse conditional block content
                const condResult = ParseHtmlJson._parseConditionalBlockMeta(parts, i, condKey, placeholders, conditionalKeys);
                i += condResult.consumed;
            }
            // Check for negative block: {{^Key}}
            // Must classify the same way generateHtmlJson_Parse routes it, or metadata and content
            // disagree: PAIRED (a {{@Key}} was already seen) is an array key; STANDALONE is a
            // conditional key.
            else if (part.startsWith('{{^') && part.endsWith('}}')) {
                const negKey = part.substring(3, part.length - 2);
                const isPairedArray = arrayKeys.includes(negKey);
                i++; // Move past {{^Key}}

                if (isPairedArray) {
                    // Parse negative block content
                    const negativeResult = ParseHtmlJson._parseArrayBlockMeta(parts, i, negKey, true, placeholders, arrayKeys, conditionalKeys);
                    i += negativeResult.consumed;
                } else {
                    if (!conditionalKeys.includes(negKey)) {
                        conditionalKeys.push(negKey);
                    }

                    // Parse conditional block content
                    const negResult = ParseHtmlJson._parseConditionalBlockMeta(parts, i, negKey, placeholders, conditionalKeys);
                    i += negResult.consumed;
                }
            }
            // Check for regular placeholder: {{PlaceholderName}}
            else if (part.startsWith('{{') && part.endsWith('}}') &&
                     !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('?')) {
                const placeholder = part.substring(2, part.length - 2);
                if (!placeholders.includes(placeholder)) {
                    placeholders.push(placeholder);
                }
                i++;
            }
            // Check for closing tags
            else if (part.startsWith('{{/')) {
                // End of current level
                return { consumed: i - startIndex };
            }
            else {
                i++;
            }
        }

        return { consumed: i - startIndex };
    }

    /**
     * Parses slot structure between {{#SlotName}} and {{/SlotName}}
     */
    private static _parseSlotStructure(
        parts: string[],
        startIndex: number,
        slotName: string,
        placeholders: string[]
    ): { slotInfo: SlotInfo; consumed: number } {
        const slotInfo = new SlotInfo();
        slotInfo.name = '#' + slotName;
        slotInfo.slots = [];

        let i = startIndex;

        while (i < parts.length) {
            const part = parts[i];

            if (!part || part.length === 0) {
                i++;
                continue;
            }

            // Check for slot close: {{/SlotName}}
            if (part === '{{/' + slotName + '}}') {
                i++; // Move past {{/SlotName}}
                break;
            }

            // Check for slot content marker: {{@PLACEHOLDER}}
            if (part.startsWith('{{@') && part.endsWith('}}')) {
                const slotContentName = part.substring(3, part.length - 2);
                i++; // Move past {{@PLACEHOLDER}}

                // Parse content inside this slot content until {{/PLACEHOLDER}}
                const slotContentInfo = new SlotContentInfo();
                slotContentInfo.name = '@' + slotContentName;
                slotContentInfo.slotholders = [];

                // Recursively parse content inside slot content (may contain nested slotholders and placeholders)
                // Pass parent's placeholders list so placeholders found in slot content are added to parent
                const nestedSlots: string[] = [];
                const nestedArrayKeys: string[] = [];
                const nestedConditionalKeys: string[] = [];
                const contentResult = ParseHtmlJson._parseTemplateContent(parts, i, placeholders, slotContentInfo.slotholders, nestedSlots, nestedArrayKeys, nestedConditionalKeys);
                i += contentResult.consumed;

                // Move past {{/PLACEHOLDER}}
                if (i < parts.length && parts[i] === '{{/' + slotContentName + '}}') {
                    i++;
                }

                slotInfo.slots.push(slotContentInfo);
            }
            else {
                i++;
            }
        }

        return { slotInfo, consumed: i - startIndex };
    }

    /**
     * Parses array block metadata to track array keys and conditional keys
     */
    private static _parseArrayBlockMeta(
        parts: string[],
        startIndex: number,
        arrayKey: string,
        isNegative: boolean,
        placeholders: string[],
        arrayKeys: string[],
        conditionalKeys: string[]
    ): { consumed: number } {
        let i = startIndex;

        while (i < parts.length) {
            const part = parts[i];

            if (!part || part.length === 0) {
                i++;
                continue;
            }

            // Check for array close: {{/ArrayKey}} (case-insensitive)
            if (part.toLowerCase() === ('{{/' + arrayKey + '}}').toLowerCase()) {
                i++; // Move past {{/ArrayKey}}
                break;
            }

            // Check for conditional block: {{?ConditionalKey}}
            if (part.startsWith('{{?') && part.endsWith('}}')) {
                const conditionalKey = part.substring(3, part.length - 2);
                if (!conditionalKeys.includes(conditionalKey)) {
                    conditionalKeys.push(conditionalKey);
                }
                i++; // Move past {{?ConditionalKey}}

                // Parse conditional block content to find nested keys
                const conditionalResult = ParseHtmlJson._parseConditionalBlockMeta(parts, i, conditionalKey, placeholders, conditionalKeys);
                i += conditionalResult.consumed;
            }
            // Check for nested array iteration: {{@NestedArrayKey}}
            else if (part.startsWith('{{@') && part.endsWith('}}')) {
                const nestedArrayKey = part.substring(3, part.length - 2);
                if (!arrayKeys.includes(nestedArrayKey)) {
                    arrayKeys.push(nestedArrayKey);
                }
                i++; // Move past {{@NestedArrayKey}}

                // Recursively parse nested array block
                const nestedResult = ParseHtmlJson._parseArrayBlockMeta(parts, i, nestedArrayKey, false, placeholders, arrayKeys, conditionalKeys);
                i += nestedResult.consumed;
            }
            // Check for regular placeholder: {{PlaceholderName}}
            else if (part.startsWith('{{') && part.endsWith('}}') &&
                     !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('$') && !part.includes('?')) {
                const placeholder = part.substring(2, part.length - 2);
                if (!placeholders.includes(placeholder)) {
                    placeholders.push(placeholder);
                }
                i++;
            }
            // Check for close tags
            else if (part.startsWith('{{/')) {
                // End of current block (don't consume the close tag)
                break;
            }
            else {
                i++;
            }
        }

        return { consumed: i - startIndex };
    }

    /**
     * Parses conditional block metadata to track conditional keys
     */
    private static _parseConditionalBlockMeta(
        parts: string[],
        startIndex: number,
        conditionalKey: string,
        placeholders: string[],
        conditionalKeys: string[]
    ): { consumed: number } {
        let i = startIndex;

        while (i < parts.length) {
            const part = parts[i];

            if (!part || part.length === 0) {
                i++;
                continue;
            }

            // Check for conditional close: {{/ConditionalKey}} or {{ /ConditionalKey}} (case-insensitive)
            if (part.toLowerCase() === ('{{/' + conditionalKey + '}}').toLowerCase() ||
                part.toLowerCase() === ('{{ /' + conditionalKey + '}}').toLowerCase()) {
                i++; // Move past close tag
                break;
            }

            // Check for regular placeholder: {{PlaceholderName}}
            if (part.startsWith('{{') && part.endsWith('}}') &&
                !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('$') && !part.includes('?')) {
                const placeholder = part.substring(2, part.length - 2);
                if (!placeholders.includes(placeholder)) {
                    placeholders.push(placeholder);
                }
                i++;
            }
            else {
                i++;
            }
        }

        return { consumed: i - startIndex };
    }

    // #endregion

    // #endregion
}
